import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { NavigationStart, Router } from "@angular/router";

// Store
import { RootReducerState } from "../store";
import { Store } from "@ngrx/store";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Helper } from "../core/helpers/helper";
import { bootRheroTheme } from "../shared/rhero-ui/rhero-theme";
import { IncomingCallService } from "../shared/services/incoming-call.service";

@Component({
  selector: "app-layout",
  templateUrl: "./layout.component.html",
  styleUrls: ["./layout.component.scss"],
  standalone: false
})

/**
 * Layout Component
 */
export class LayoutComponent implements OnInit, OnDestroy {
  layoutType!: string;
  private veilTimers: ReturnType<typeof setTimeout>[] = [];
  private navigationSubscription?: Subscription;
  private layoutSubscription?: Subscription;

  constructor(
    private store: Store<RootReducerState>,
    private helper: Helper,
    private incomingCallService: IncomingCallService,
    private router: Router,
    private ngbModal: NgbModal
  ) {}

  ngOnDestroy(): void {
    document.body.classList.remove("rhero-topnav", "rhero-shell", "rhero-nomap");
    this.veilTimers.forEach((id) => clearTimeout(id));
    this.incomingCallService.destroy();
    this.navigationSubscription?.unsubscribe();
    this.layoutSubscription?.unsubscribe();
    document.removeEventListener("click", this.onDocumentClick, true);
  }

  ngOnInit(): void {
    document.body.classList.add("rhero-topnav", "rhero-shell");
    this.watchHistory();
    this.veilBrokenMap();
    [500, 1400, 2800].forEach((ms) => this.veilTimers.push(setTimeout(() => this.veilBrokenMap(), ms)));
    // Spec #1 — start the global incoming-call listener as soon as the
    // dispatcher's profile is known. We retry briefly if the profile arrives
    // after this hook (auth flow can race the layout).
    this.tryInitIncomingCall();
    this.layoutSubscription = this.store.select("layout").subscribe((data) => {
      // A saved "horizontal" theme paints the old top bar over this shell. Always vertical.
      const layout = { ...data, LAYOUT: "vertical" };
      this.layoutType = "vertical";
      document.documentElement.setAttribute("data-layout", "vertical");
      document.documentElement.setAttribute("data-theme", data.LAYOUT_THEME);
      document.documentElement.setAttribute("data-theme-colors", data.LAYOUT_THEME_COLOR);
      document.documentElement.setAttribute("data-bs-theme", data.LAYOUT_MODE);
      document.documentElement.classList.toggle("dark", data.LAYOUT_MODE === "dark");
      document.documentElement.setAttribute("data-layout-width", data.LAYOUT_WIDTH);
      document.documentElement.setAttribute("data-layout-position", data.LAYOUT_POSITION);
      document.documentElement.setAttribute("data-topbar", data.TOPBAR);

      if (data.LAYOUT === "vertical" || data.LAYOUT === "twocolumn" || data.LAYOUT === "semibox") {
        document.documentElement.setAttribute("data-sidebar", data.SIDEBAR_COLOR);
      }
      if (data.LAYOUT === "vertical" || data.LAYOUT === "twocolumn") {
        document.documentElement.setAttribute("data-sidebar-size", data.SIDEBAR_SIZE);
      }
      if (data.LAYOUT === "vertical" || data.LAYOUT === "twocolumn") {
        document.documentElement.setAttribute("data-sidebar-image", data.SIDEBAR_IMAGE);
      }
      if (data.LAYOUT === "vertical" || data.LAYOUT === "twocolumn") {
        document.documentElement.setAttribute("data-layout-style", data.SIDEBAR_VIEW);
      }

      document.documentElement.setAttribute("data-preloader", data.DATA_PRELOADER);
      document.documentElement.setAttribute("data-sidebar-visibility", data.SIDEBAR_VISIBILITY);
      // Store data in localStorage
      localStorage.setItem("themeLayoutData", JSON.stringify(layout));
      const theme = bootRheroTheme();
      this.helper.isDarkModeOn = theme.mode === "dark";
    });
  }

  /**
   * Check if the vertical layout is requested
   */
  isVerticalLayoutRequested(): boolean {
    return this.layoutType === "vertical";
  }

  /**
   * Check if the semibox layout is requested
   */
  isSemiboxLayoutRequested(): boolean {
    return this.layoutType === "semibox";
  }

  /**
   * Check if the horizontal layout is requested
   */
  isHorizontalLayoutRequested(): boolean {
    return this.layoutType === "horizontal";
  }

  /**
   * Check if the horizontal layout is requested
   */
  isTwoColumnLayoutRequested(): boolean {
    return this.layoutType === "twocolumn";
  }

  /** Routing owns browser history; overlays close when entering another page. */
  private watchHistory(): void {
    document.addEventListener("click", this.onDocumentClick, true);
    this.navigationSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) this.ngbModal.dismissAll("Navigation");
    });
  }

  private onDocumentClick = (event: MouseEvent): void => {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const backIcon = target.closest(".ri-arrow-left-line");
    if (backIcon && !backIcon.closest(".pagination, .ngx-pagination")) {
      event.preventDefault();
      event.stopPropagation();
      history.back();
      return;
    }
    const anchor = target.closest("a");
    if (!anchor) return;
    const href = anchor.getAttribute("href");
    if (href === "#" || href === "" || (href && href.startsWith("javascript:"))) {
      event.preventDefault();
    }
  };

  private veilBrokenMap(): void {
    document.body.classList.toggle("rhero-nomap", !this.helper.mapKey);
    if (this.helper.mapKey) return;
    document.querySelectorAll("div").forEach((el) => {
      const text = el.textContent || "";
      const height = el.getBoundingClientRect().height;
      if (text.includes("can't load Google Maps") && height > 20 && height < 220 && el.children.length < 8) {
        (el as HTMLElement).style.display = "none";
      }
    });
  }

  private tryInitIncomingCall(attempt: number = 0): void {
    const ready =
      !!this.helper.dispatcherDetails?.entity?.countryId &&
      !!(this.incomingCallService as any)["socketService"]?.socket;
    if (ready) {
      this.incomingCallService.init();
      return;
    }
    if (attempt >= 60) {
      console.warn("[IncomingCall] gave up waiting for profile + socket after 30s");
      return;
    }
    setTimeout(() => this.tryInitIncomingCall(attempt + 1), 500);
  }
}
